import { useEffect, useRef, useState } from 'preact/hooks';
import { apiFetch } from '../api/client';
import { ASSISTANT_NAME } from '../brand.js';

const endpoint = '/v1/owner/booking-site';
const emptyProfile = { name: '', introduction: '', address: '', phone: '', website: '', theme: 'citrus' };
const fieldStyle = { width: '100%', padding: '0.65rem', border: '1px solid var(--border-subtle)', borderRadius: '8px', background: 'white', color: 'var(--text-primary)', font: 'inherit', boxSizing: 'border-box' };
const buttonStyle = { padding: '0.7rem 1rem', borderRadius: '8px', border: '1px solid var(--border-subtle)', background: 'white', font: 'inherit', cursor: 'pointer' };

export function BookingSiteSettings({ salonName, eligibilityRevision = 0 }) {
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState({ slug: '', profile: { ...emptyProfile, name: salonName || '' } });
  const [busy, setBusy] = useState('loading');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [reloadRequired, setReloadRequired] = useState(false);
  const [preview, setPreview] = useState('');
  const form = useRef(null);
  const alive = useRef(true);
  const inFlight = useRef(true);
  const checkedRevision = useRef(eligibilityRevision);
  const [readinessError, setReadinessError] = useState('');
  const [retryRevision, setRetryRevision] = useState(0);
  const pendingReadiness = checkedRevision.current !== eligibilityRevision || !!readinessError;

  // Serialize this read behind edits already being saved. Only readiness fields
  // may be refreshed: adopting a new version would silently rebase a stale draft.
  useEffect(() => {
    if (inFlight.current || !saved || checkedRevision.current === eligibilityRevision) return;
    inFlight.current = true;
    checkedRevision.current = eligibilityRevision;
    setBusy('readiness');
    setReadinessError('');
    apiFetch(endpoint).then(snapshot => {
      if (!alive.current) return;
      if (snapshot.version !== saved.version || snapshot.slug !== saved.slug) {
        setReloadRequired(true);
        setReadinessError('The saved page changed in another session. Your edits are still here. Copy any changes you want to keep, then reload the saved page.');
        return;
      }
      setSaved(current => ({ ...current, can_save: snapshot.can_save, can_publish: snapshot.can_publish,
        publication_blockers: snapshot.publication_blockers, live: snapshot.live }));
    }).catch(() => {
      if (alive.current) setReadinessError('Could not refresh booking readiness. Your edits are still here.');
    }).finally(() => {
      inFlight.current = false;
      if (alive.current) setBusy('');
    });
  }, [eligibilityRevision, retryRevision, busy, saved]);

  const populate = (snapshot) => {
    setSaved(snapshot);
    setDraft({ slug: snapshot.slug, profile: { ...snapshot.profile, name: snapshot.profile.name || salonName || '' } });
    setReloadRequired(false);
    setReadinessError('');
  };

  useEffect(() => {
    alive.current = true;
    apiFetch(endpoint).then(data => {
      if (alive.current) populate(data);
    }).catch(err => {
      if (alive.current) setError(err.message);
    }).finally(() => {
      inFlight.current = false;
      if (alive.current) setBusy('');
    });
    return () => { alive.current = false; };
  }, []);

  const change = (key, value) => {
    setDraft(current => key === 'slug' ? { ...current, slug: value } : { ...current, profile: { ...current.profile, [key]: value } });
    setPreview('');
    setNotice('');
  };

  const reload = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy('loading');
    setError('');
    try {
      const snapshot = await apiFetch(endpoint);
      if (alive.current) { populate(snapshot); setPreview(''); setNotice('Loaded the saved page.'); }
    } catch (err) {
      if (alive.current) setError(err.message);
    } finally {
      inFlight.current = false;
      if (alive.current) setBusy('');
    }
  };

  const save = async (published, useSaved = false) => {
    if (inFlight.current || reloadRequired || (published && pendingReadiness) || !saved || (!useSaved && !form.current.reportValidity())) return;
    inFlight.current = true;
    setBusy('saving'); setError(''); setNotice('');
    let committed = false;
    try {
      const receipt = await apiFetch(endpoint, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: useSaved ? saved.slug : draft.slug, expected_version: saved.version, published, profile: useSaved ? saved.profile : draft.profile }),
      });
      committed = true;
      const snapshot = await apiFetch(endpoint);
      if (!alive.current) return;
      if (snapshot.version !== receipt.version) throw new Error('The saved page changed in another session.');
      if (useSaved) setSaved(snapshot);
      else populate(snapshot);
      setNotice(published ? 'Your page is published. Share the link with your clients.' : 'Page saved and unpublished.');
      setPreview('');
    } catch (err) {
      if (!alive.current) return;
      const uncertain = committed || !err.status || err.status >= 500 || err.status === 409;
      setReloadRequired(uncertain);
      setError(uncertain ? `${err.message} Your edits are still here. Copy any changes you want to keep, then reload the saved page before trying again.` : err.message);
    } finally {
      inFlight.current = false;
      if (alive.current) setBusy('');
    }
  };

  const showPreview = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy('preview'); setError('');
    try {
      const result = await apiFetch(endpoint + '/preview', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ profile: draft.profile }) });
      if (alive.current) setPreview(result.html);
    } catch (err) {
      if (alive.current) setError(err.message);
    } finally {
      inFlight.current = false;
      if (alive.current) setBusy('');
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(saved.public_url); if (alive.current) setNotice('Link copied.'); }
    catch { if (alive.current) setError('Could not copy automatically. Select and copy the link below.'); }
  };

  return (
    <section class="card" aria-labelledby="booking-site-title" style={{ padding: '1.25rem' }}>
      <h2 id="booking-site-title" style={{ margin: '0 0 0.5rem', fontSize: '1.125rem', fontWeight: 600 }}>Your booking page</h2>
      <p style={{ color: 'var(--text-secondary)', marginTop: 0 }}>Give clients one link to chat with {ASSISTANT_NAME} and book an appointment.</p>
      {error && <p role="alert" style={{ color: 'var(--coral)', overflowWrap: 'anywhere' }}>{error}</p>}
      {readinessError && <div role="alert"><p>{readinessError}</p>{!reloadRequired && <button type="button" style={buttonStyle} disabled={!!busy} onClick={() => { checkedRevision.current = -1; setRetryRevision(value => value + 1); }}>Retry booking readiness</button>}</div>}
      {notice && <p role="status">{notice}</p>}
      {!saved ? <>
        {busy ? <p role="status">Loading your page settings…</p> : <button style={buttonStyle} onClick={reload}>Retry loading page</button>}
      </> : <>
        <p><strong>{saved.live ? 'Published' : saved.published ? 'Published · booking unavailable' : 'Unpublished'}</strong></p>
        {saved.public_url && <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
          <input aria-label="Your booking link" readOnly value={saved.public_url} onFocus={event => event.currentTarget.select()} style={{ ...fieldStyle, flex: '1 1 220px', minWidth: 0 }} />
          <button style={buttonStyle} type="button" onClick={copyLink}>Copy link</button>
          {saved.live && <a href={saved.public_url} target="_blank" rel="noopener noreferrer" style={buttonStyle}>Open page</a>}
        </div>}
        {!saved.can_save && <p>Connect Square before saving your page. You can edit and preview it now.</p>}
        {saved.publication_blockers?.length > 0 && <div role="note">{saved.publication_blockers.map(text => <p key={text} style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{text}</p>)}</div>}
        <form ref={form} onSubmit={event => { event.preventDefault(); save(saved.published); }}>
          <fieldset disabled={!!busy} style={{ border: 0, padding: 0, margin: 0, minWidth: 0, display: 'grid', gap: '1rem' }}>
            <label>Booking link <span style={{ display: 'block', fontSize: '0.8125rem' }}>book.gojonathan.ai/</span>
              <input aria-label="Booking link name" required pattern="[a-z0-9]+(-[a-z0-9]+)*" minLength={3} maxLength={63} readOnly={saved.version > 0} value={draft.slug} onInput={event => change('slug', event.currentTarget.value)} style={fieldStyle} />
              <span style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>Choose carefully: this link stays the same after your first save, even while unpublished.</span>
            </label>
            <Field label="Salon name" name="name" draft={draft} change={change} required maxLength={120} />
            <label>Introduction<textarea rows={3} maxLength={500} value={draft.profile.introduction} onInput={event => change('introduction', event.currentTarget.value)} style={fieldStyle} /></label>
            <Field label="Address" name="address" draft={draft} change={change} maxLength={240} />
            <Field label="Public phone" name="phone" draft={draft} change={change} type="tel" placeholder="+12125550123" pattern="\+[1-9][0-9]{6,14}" maxLength={16} />
            <Field label="Website" name="website" draft={draft} change={change} type="url" placeholder="https://yoursalon.com" maxLength={2048} />
            <label>Page style<select value={draft.profile.theme} onChange={event => change('theme', event.currentTarget.value)} style={fieldStyle}>
              <option value="citrus">Citrus · warm and bright</option><option value="forest">Forest · calm and natural</option><option value="ink">Ink · simple and bold</option>
            </select></label>
            {saved.published && <p style={{ margin: 0, fontSize: '0.875rem' }}>Saving changes updates your published page immediately.</p>}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              <button type="button" style={buttonStyle} onClick={showPreview}>Preview page</button>
              <button type="submit" style={buttonStyle} disabled={reloadRequired || !saved.can_save || (saved.published && (!saved.can_publish || pendingReadiness))}>{busy === 'saving' ? 'Saving…' : saved.published ? 'Save changes' : 'Save page'}</button>
              {!saved.published && <button type="button" style={{ ...buttonStyle, background: 'var(--sage)', color: 'white' }} disabled={reloadRequired || pendingReadiness || !saved.can_publish} onClick={() => save(true)}>Publish page</button>}
              {saved.published && <button type="button" style={buttonStyle} disabled={reloadRequired} onClick={() => save(false, true)}>Unpublish page</button>}
            </div>
          </fieldset>
        </form>
        {reloadRequired && <button type="button" style={{ ...buttonStyle, marginTop: '1rem' }} disabled={!!busy} onClick={reload}>Reload saved page</button>}
        {preview && <div style={{ marginTop: '1rem' }}>
          <p>Preview only. Booking is disabled here.</p>
          <iframe title="Booking page preview" srcDoc={preview} sandbox="" referrerPolicy="no-referrer" style={{ width: '100%', height: '580px', border: '1px solid var(--border-subtle)', borderRadius: '8px', background: 'white' }} />
        </div>}
      </>}
    </section>
  );
}

function Field({ label, name, draft, change, ...attributes }) {
  return <label>{label}<input {...attributes} value={draft.profile[name]} onInput={event => change(name, event.currentTarget.value)} style={fieldStyle} /></label>;
}
